import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  UnorderedList,
  ListItem,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';

interface ImportErrorDisplayProps {
  error?: string | null;
  warnings?: string[];
  showDetails?: boolean;
}

/**
 * Component for displaying import errors and warnings with proper formatting
 */
const ImportErrorDisplay: React.FC<ImportErrorDisplayProps> = ({ 
  error, 
  warnings = [], 
  showDetails = true 
}) => {
  if (!error && warnings.length === 0) {
    return null;
  }

  const formatErrorText = (errorText: string): React.ReactNode => {
    // Check if error contains bullet points or line breaks
    if (errorText.includes('•') || errorText.includes('\n')) {
      const lines = errorText.split('\n').filter(line => line.trim());
      return (
        <UnorderedList spacing={1}>
          {lines.map((line, index) => (
            <ListItem key={index} fontSize="sm">
              {line.replace(/^•\s*/, '')}
            </ListItem>
          ))}
        </UnorderedList>
      );
    }
    
    return <Text fontSize="sm">{errorText}</Text>;
  };

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert status="error" mb={warnings.length > 0 ? 3 : 0}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize="md">Import Failed!</AlertTitle>
            <AlertDescription>
              {formatErrorText(error)}
            </AlertDescription>
          </Box>
        </Alert>
      )}
      
      {/* Warning Alert */}
      {warnings.length > 0 && (
        <Alert status="warning">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize="md">
              Import Warnings ({warnings.length})
            </AlertTitle>
            <AlertDescription>
              {warnings.length === 1 ? (
                <Text fontSize="sm" mt={1}>{warnings[0]}</Text>
              ) : showDetails ? (
                <Box mt={2}>
                  <Text fontSize="sm" mb={2}>
                    The following issues were detected but import can continue:
                  </Text>
                  <UnorderedList spacing={1}>
                    {warnings.map((warning, index) => (
                      <ListItem key={index} fontSize="sm">
                        {warning}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              ) : (
                <Accordion allowToggle size="sm" mt={2}>
                  <AccordionItem border="none">
                    <AccordionButton p={0} _hover={{ bg: 'transparent' }}>
                      <Text fontSize="sm" flex="1" textAlign="left">
                        Click to view warning details
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={2} px={0}>
                      <UnorderedList spacing={1}>
                        {warnings.map((warning, index) => (
                          <ListItem key={index} fontSize="sm">
                            {warning}
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}
            </AlertDescription>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default ImportErrorDisplay;